import React, { useEffect, useMemo, useState } from "react";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";

// Utils
import { getAlert, parseCurrency } from "../../../../utils/SamuiUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions,
} from "../../../../utils/DataTableKeyboardNavigation";

function Main({ masterList = [], name, onPageInsert, onRowSelected, activeAfterSaveId, onClearActiveAfterSave, idField = "WH_Id" }) {
  const [dataMasterList, setDataMasterList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);

  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const rowRefs = React.useRef([]);

  useEffect(() => {
    try {
      if (Array.isArray(masterList)) {
        setDataMasterList(masterList);
      } else {
        setDataMasterList([]);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  }, [masterList]);

  useEffect(() => {
    const lowercased = searchTerm.trim().toLowerCase();
    if (!lowercased) {
      setFilteredItems(dataMasterList);
      return;
    }

    const filtered = dataMasterList.filter((item) => {
      if (!item) return false;
      const valuesToSearch = [
        item.WH_Code,
        item.WH_Name,
        item.WH_Status === "Y" ? "ใช้งาน" : "ไม่ใช้งาน",
      ];
      return valuesToSearch.some(
        (value) =>
          typeof value === "string" && value.toLowerCase().includes(lowercased)
      );
    });
    setFilteredItems(filtered);
  }, [searchTerm, dataMasterList]);

  useEffect(() => {
    if (activeAfterSaveId == null) {
      setCurrentPage(1);
      setSelectedRowIndex(0);
    }
  }, [filteredItems]);

  useEffect(() => {
    if (activeAfterSaveId == null || filteredItems.length === 0) return;
    const idx = filteredItems.findIndex(item => parseCurrency(item[idField]) === parseCurrency(activeAfterSaveId));
    if (idx >= 0) {
      const page = Math.floor(idx / itemsPerPage) + 1;
      setCurrentPage(page);
      setSelectedRowIndex(idx % itemsPerPage);
      onClearActiveAfterSave?.();
      setTimeout(() => {
        rowRefs.current[idx % itemsPerPage]?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
      }, 100);
    } else {
      onClearActiveAfterSave?.();
    }
  }, [activeAfterSaveId, filteredItems, idField, itemsPerPage, onClearActiveAfterSave]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1) return;
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
    const nextPage = Math.min(pageNumber, totalPages);
    setCurrentPage(nextPage);
    setSelectedRowIndex(0);
  };

  const handleItemsPerPageChange = (event) => {
    const nextValue = parseCurrency(event.target.value);
    setItemsPerPage(nextValue || 50);
    setCurrentPage(1);
    setSelectedRowIndex(0);
  };

  const renderPageNumbers = () => {
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
    const pages = [];
    const maxVisiblePages = 5;
    const range = 2;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <li
            key={i}
            className={`paginate_button page-item ${
              currentPage === i ? "active" : ""
            }`}
          >
            <button
              type="button"
              className="page-link btn"
              onClick={() => handlePageChange(i)}
            >
              {i}
            </button>
          </li>
        );
      }
      return pages;
    }

    pages.push(
      <li
        key={1}
        className={`paginate_button page-item ${
          currentPage === 1 ? "active" : ""
        }`}
      >
        <button
          type="button"
          className="page-link btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      </li>
    );

    if (currentPage - range > 2) {
      pages.push(
        <li key="ellipsis-start" className="paginate_button page-item">
          <span className="page-link">...</span>
        </li>
      );
    }

    for (
      let i = Math.max(currentPage - range, 2);
      i <= Math.min(currentPage + range, totalPages - 1);
      i++
    ) {
      pages.push(
        <li
          key={i}
          className={`paginate_button page-item ${
            currentPage === i ? "active" : ""
          }`}
        >
          <button
            type="button"
            className="page-link btn"
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    if (currentPage + range < totalPages - 1) {
      pages.push(
        <li key="ellipsis-end" className="paginate_button page-item">
          <span className="page-link">...</span>
        </li>
      );
    }

    pages.push(
      <li
        key={totalPages}
        className={`paginate_button page-item ${
          currentPage === totalPages ? "active" : ""
        }`}
      >
        <button
          type="button"
          className="page-link btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      </li>
    );

    return pages;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() => {
    return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredItems, indexOfFirstItem, indexOfLastItem]);

  const handleRowSelected = (data) => {
    if (typeof onRowSelected !== "function") {
      return;
    }

    if (data && typeof data === "object") {
      onRowSelected(data);
      return;
    }

    const foundItem = filteredItems.find((item) => item?.WH_Id === data);

    if (foundItem) {
      onRowSelected(foundItem);
    }
  };

  const fieldMappings = {
    no: "WH_Id",
  };

  useDataTableKeyboardNavigation(
    true,
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    handleRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs
          page={name}
          items={[
            { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
            { name: name, url: "/tbsetwh" },
          ]}
        />
      </div>

      <div
        className="p-3 rounded-3 my-2 shadow-lg"
        style={{ backgroundColor: "#ffff" }}
      >
        <div className="row">
          <div className="col-3 d-flex">
            <div className="navbar-form nav-search">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="ค้นหาคลังสินค้า..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
              </div>
            </div>
          </div>
          <div className="col-9 d-flex justify-content-end">
            <button
              onClick={onPageInsert}
              className="btn btn-warning text-white ms-2"
              type="button"
            >
              สร้าง{name} <i className="fa fa-plus" />
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card p-2">
            <div
              className="table-responsive"
              style={{ maxHeight: "600px", overflowY: "auto" }}
            >
              <table
                id="basic-datatables"
                className="table table-striped table-hover"
              >
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "8%" }}>
                      #
                    </th>
                    <th className="text-center" style={{ width: "20%" }}>
                      รหัสคลัง
                    </th>
                    <th className="text-center" style={{ width: "54%" }}>
                      ชื่อคลัง
                    </th>
                    <th className="text-center" style={{ width: "12%" }}>
                      สถานะ
                    </th>
                    <th className="text-center" style={{ width: "6%" }}>
                      แก้ไข
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr
                        key={item.WH_Id || index}
                        ref={el => rowRefs.current[index] = el}
                        style={getDataTableRowStyle(index, selectedRowIndex)}
                        onClick={() => handleRowSelected(item)}
                      >
                        <td className="text-center">
                          {indexOfFirstItem + index + 1}
                        </td>
                        <td className="text-left">{item.WH_Code || "-"}</td>
                        <td className="text-left">{item.WH_Name || "-"}</td>
                        <td className="text-center">
                          {item.WH_Status === "Y" ? "ใช้งาน" : "ไม่ใช้งาน"}
                        </td>
                        <td className="text-center">
                          <i
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowSelected(item);
                            }}
                            className="fas fa-edit fw-bold"
                            style={{ color: "#EF6C00", cursor: "pointer" }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-2">
              <small className="text-muted">
                {getDataTableKeyboardInstructions(false, true)}
              </small>
            </div>

            <div className="row mt-3">
              <div className="col-12 d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <label htmlFor="itemsPerPage" className="me-2">
                    Items per page:
                  </label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="form-select"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={150}>150</option>
                    <option value={dataMasterList.length}>All</option>
                  </select>
                </div>

                <div className="dataTables_paginate paging_simple_numbers">
                  <ul className="pagination">
                    <li
                      className={`paginate_button page-item previous ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {renderPageNumbers()}
                    <li
                      className={`paginate_button page-item next ${
                        currentPage ===
                          Math.ceil(filteredItems.length / itemsPerPage) ||
                        filteredItems.length === 0
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={
                          currentPage ===
                            Math.ceil(filteredItems.length / itemsPerPage) ||
                          filteredItems.length === 0
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Main;
